import { useCallback, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import useGetCatalogueData from "../apis/useGetCatalogueData/useGetCatalogueData";
import useGetConfig from "../apis/useGetConfig/useGetConfig";
import { publicBaseUrl } from "../common/envconst";
import { useCartUserTracking } from "../common/hooks/useCartUserTracking";
import { useCookie } from "../common/hooks/useCookie";
import { useLinkPayCookies } from "../common/hooks/useLinkPayCookies";
import { useCartActions } from "../contexts/CartServiceContext/CartServiceContext";
import { useConfigActions, useConfigContext } from "../contexts/ConfigContext/ConfigContext";
import { APP_MODE } from "../contexts/ConfigContext/types";
import { usePageViewTracking } from "./hooks/usePageViewTracking";

type INavigateWithParams = (path: string, params?: Record<string, string>) => void;
export const useNavigateWithParams = () => {
	const navigate = useNavigate();
	const location = useLocation();

	const navigateWithParams: INavigateWithParams = useCallback(
		(path, params) => {
			// Preserve existing uuid parameter
			const currentParams = new URLSearchParams(location.search);
			const uuid = currentParams.get("uuid");

			const searchParams = new URLSearchParams(params || {});

			// If current URL has uuid, preserve it
			if (uuid) {
				searchParams.set("uuid", uuid);
			}

			if (searchParams.toString()) {
				navigate(`${path}?${searchParams.toString()}`);
			} else {
				navigate(path);
			}
		},
		[location, navigate]
	);

	return { navigateWithParams };
};

interface IUrlParams {
	[key: string]: string;
}

export const useUrlParams = (): IUrlParams => {
	const location = useLocation();

	const params: IUrlParams = useMemo(() => {
		const queryParams = new URLSearchParams(location.search);
		const paramsObject: IUrlParams = {};

		for (const [key, value] of queryParams.entries()) {
			paramsObject[key] = value;
		}

		return paramsObject;
	}, [location.search]);

	return params;
};

export const getUUIDFromUrl = (): string | null => {
	if (typeof globalThis === "undefined" || !globalThis.location) {
		return null;
	}

	const urlParams = new URLSearchParams(globalThis.location.search);

	return urlParams.get("uuid");
};

export const useAppInit = () => {
	const configActions = useConfigActions();
	const cartActions = useCartActions();
	const urlUuid = getUUIDFromUrl();
	const { state: stallMode } = useConfigContext((state) => state.config?.stall.mode);

	const { isFullySetup } = useLinkPayCookies();
	const { cookieValue: storedLocationId, setCookie: setLocationCookie } = useCookie(
		"location_id",
		"",
		{
			expires: 0.5,
			secure: true,
			sameSite: "strict" as const,
			path: publicBaseUrl,
		}
	);

	// Track user UID changes and manage cart accordingly
	// Handles user state transitions:
	// - Guest -> User (login): inherit guest cart
	// - User -> Guest (logout): clear cart
	// - User A -> User B (switch): clear cart
	useCartUserTracking();

	usePageViewTracking();

	const locationId = useMemo(() => {
		// 1. Prefer the UUID carried by the current URL — primary source of truth.
		if (urlUuid) {
			setLocationCookie(urlUuid);

			return urlUuid;
		}

		// 2. Fall back to the persisted cookie when the URL does not carry a UUID
		//    (e.g. navigation within the SPA after the initial QR landing).
		if (storedLocationId) {
			return storedLocationId;
		}

		// 3. No UUID anywhere — fail loud rather than silently substituting a
		//    hardcoded location. The UI layer (showLoadingCover gate in App.tsx)
		//    will keep the splash/loader visible until a real locationId arrives.
		return null;
	}, [urlUuid, storedLocationId, setLocationCookie]);

	// Call API hooks to load config and catalog data
	const configResult = useGetConfig();
	const catalogResult = useGetCatalogueData();

	useEffect(() => {
		if (locationId) {
			configActions.INIT_LOCATION_ID({ location_id: locationId });

			// SOK no need cart cache: FOOD-1748
			// cartActions.CART_INIT();
		}
	}, [locationId, configActions, cartActions, stallMode]);

	useEffect(() => {
		document.title = "SOK";
		if (isFullySetup) {
			configActions.SET_APP_MODE(APP_MODE.NORMAL);
		} else {
			configActions.SET_APP_MODE(APP_MODE.NORMAL);
		}
	}, [stallMode, configActions, isFullySetup]);

	// Return loading state for splash screen
	return {
		isLoading: (configResult?.isLoading ?? true) || (catalogResult?.isLoading ?? true),
		hasData: !!configResult?.data && !!catalogResult?.data,
		configError: configResult?.error,
		catalogError: catalogResult?.error,
	};
};
